// --- Enums (match backend) ---

export enum WorkOrderStatusCode {
  Created = 1,
  Assigned = 2,
  Scheduled = 3,
  InProgress = 4,
  OnHold = 5,
  Completed = 6,
  Verified = 7,
  Cancelled = 8,
  PendingReview = 9,
}

export enum WorkOrderTypeCode {
  Reactive = 1,
  PreventiveMaintenance = 2,
  PredictiveMaintenance = 3,
  Inspection = 4,
  GeneralTask = 5,
}

export enum WorkOrderPriorityCode {
  Critical = 1,
  High = 2,
  Normal = 3,
  Low = 4,
}

// --- DTOs ---

export interface WorkOrder {
  id: string;
  number: string;
  title: string;
  description?: string;
  workInstructions?: string;
  workOrderTypeId: number;
  workOrderTypeName: string;
  workOrderStatusId: number;
  workOrderStatusName: string;
  priorityCode: number;
  machineId?: string;
  machineName?: string;
  machineSerialNumber?: string;
  locationId?: string;
  locationName?: string;
  parentLocationName?: string;
  assignedToUserId?: string;
  assignedToUserName?: string;
  dueDate?: string;
  scheduledStartDate?: string;
  scheduledEndDate?: string;
  actualStartDate?: string;
  actualEndDate?: string;
  estimatedDurationMinutes?: number;
  serviceReportId?: string;
  serviceRequestId?: string;
  parentWorkOrderId?: string;
  recurrenceParentId?: string;
  teamId?: string;
  boardPosition?: number;
  createdByUserName?: string;
  createdDate: string;
  modifiedDate: string;
  assignees?: WorkOrderAssignee[];
  linkedServiceReports?: LinkedServiceReport[];
}

export interface LinkedServiceReport {
  serviceReportId: string;
  serviceReportNumber?: string;
  linkedDate: string;
  linkedByUserName?: string;
}

export interface WorkOrderDetail extends WorkOrder {
  completionNotes?: string;
  cost?: number;
  scheduledEndDate?: string;
  actualDurationMinutes?: number;
  assignedByUserId?: string;
  assignedByUserName?: string;
  createdByUserId?: string;
  recurrenceRule?: string;
  recurrenceParentId?: string;
  teamName?: string;
  parentWorkOrderNumber?: string;
  parentWorkOrderTitle?: string;
  parentWorkOrderStatusId?: number;
  files: WorkOrderFile[];
  progressUpdates: WorkOrderProgressUpdate[];
  childWorkOrders?: WorkOrder[];
}

export interface WorkOrderFile {
  id: string;
  fileName: string;
  filePath: string;
  fileSize: number;
  contentType: string;
  uploadedByUserName?: string;
  createdDate: string;
  /**
   * WO-CLOSING-PHOTOS-0414: Discriminates general WO attachments from photos
   * uploaded via CompletionDialog at closing time. The Maintenance WebApi does
   * not register a JsonStringEnumConverter so the enum serializes as a number
   * (General=1, ClosingRemarks=2) — but we also accept the string form for
   * forward-compatibility. Treat missing/undefined as General.
   */
  uploadContext?: "General" | "ClosingRemarks" | 1 | 2;
}

/** WO-CLOSING-PHOTOS-0414: Helper to normalize the upload context discriminator. */
export const isClosingRemarksFile = (f: { uploadContext?: WorkOrderFile["uploadContext"] }): boolean =>
  f.uploadContext === "ClosingRemarks" || f.uploadContext === 2;

export interface WorkOrderProgressUpdate {
  id: string;
  updateText: string;
  oldStatusId?: number;
  newStatusId?: number;
  oldStatusName?: string;
  newStatusName?: string;
  createdByUserName?: string;
  createdDate: string;
}

export interface WorkOrderSummary {
  created: number;
  assigned: number;
  scheduled: number;
  inProgress: number;
  onHold: number;
  completed: number;
  verified: number;
  pendingReview: number;
  cancelled: number;
  total: number;
}

export interface WorkOrderFilters {
  statuses: FilterOption[];
  types: FilterOption[];
  assignees: AssigneeFilterOption[];
  machines: MachineFilterOption[];
}

export interface FilterOption {
  id: number;
  name: string;
  count: number;
}

export interface AssigneeFilterOption {
  userId: string;
  userName: string;
  count: number;
}

export interface MachineFilterOption {
  machineId: string;
  machineName: string;
  count: number;
}

// --- Query ---

export interface WorkOrdersQuery {
  pageNumber: number;
  pageSize: number;
  statusIds?: number[];
  typeIds?: number[];
  priorityCode?: number;
  machineId?: string;
  assignedToUserId?: string;
  searchTerm?: string;
  sortBy?: string;
  sortDirection?: string;
  dateFrom?: string;
  dateTo?: string;
}

// --- Create/Update ---

export interface CreateWorkOrder {
  title: string;
  description?: string;
  workInstructions?: string;
  workOrderTypeId: number;
  priorityCode: number;
  machineId?: string;
  locationId?: string;
  assignedToUserId?: string;
  assignedToUserName?: string;
  dueDate?: string;
  scheduledStartDate?: string;
  scheduledEndDate?: string;
  estimatedDurationMinutes?: number;
  serviceReportId?: string;
  serviceReportNumber?: string;
  parentWorkOrderId?: string;
  recurrenceRule?: string;
  teamId?: string;
  autoAssignToLeastLoaded?: boolean;
}

export interface UpdateWorkOrder extends CreateWorkOrder {
  completionNotes?: string;
  cost?: number;
}

export interface ChangeWorkOrderStatus {
  newStatusId: number;
  notes?: string;
  completionNotes?: string;
  cost?: number;
  createServiceReport: boolean;
}

export interface AssignWorkOrder {
  assignedToUserId: string;
  assignedToUserName: string;
}

export interface AddProgressUpdate {
  updateText: string;
}

// --- Recurrence ---

export interface PreviewRecurrenceRequest {
  recurrenceRule: string;
  startDate: string;
  count?: number;
}

export interface PreviewRecurrenceResponse {
  occurrences: string[];
  humanReadable: string;
}

export interface GenerateRecurringResponse {
  generatedCount: number;
}

export interface RegenerateFutureResponse {
  deletedCount: number;
  generatedCount: number;
}

export interface CancelFutureResponse {
  cancelledCount: number;
}

// --- Escalation (Phase 2C) ---

export interface EscalateWorkOrder {
  issue?: string;
  description?: string;
}

export interface EscalateResponse {
  serviceRequestId: string;
  serviceRequestNumber: string;
}

// --- Teams (Phase 3B) ---

export interface Team {
  id: string;
  name: string;
  description?: string;
  leadUserId?: string;
  leadUserName?: string;
  isActive: boolean;
  memberCount: number;
  members?: TeamMember[];
  locations?: TeamLocation[];
}

export interface TeamLocation {
  locationId: string;
  locationName: string;
  parentLocationName?: string;
  createdAt: string;
}

export interface TeamMember {
  userId: string;
  userName: string;
  joinedDate: string;
  openWorkOrderCount?: number;
}

export interface CreateTeam {
  name: string;
  description?: string;
  leadUserId?: string;
  leadUserName?: string;
}

export interface TeamWorkload {
  teamId: string;
  teamName: string;
  members: TeamMemberWorkload[];
}

export interface TeamMemberWorkload {
  userId: string;
  userName: string;
  openCount: number;
  overdueCount: number;
  completedCount: number;
}

// --- Analytics (Phase 3C) ---

export interface WorkOrderAnalytics {
  meanTimeToRepairHours: number;
  meanTimeBetweenFailuresHours: number;
  completionRate: number;
  overdueCount: number;
  totalCost: number;
  totalCompleted: number;
  totalCreated: number;
  completionTrend: PeriodMetric[];
  createdTrend: PeriodMetric[];
  mttrTrend: PeriodMetric[];
  costTrend: PeriodMetric[];
  byType: TypeBreakdown[];
  byPriority: PriorityBreakdown[];
  topMachines: MachineBreakdown[];
  topAssignees: AssigneeBreakdown[];
}

export interface PeriodMetric {
  periodStart: string;
  label: string;
  value: number;
}

export interface TypeBreakdown {
  typeId: number;
  typeName: string;
  count: number;
}

export interface PriorityBreakdown {
  priorityCode: number;
  priorityName: string;
  count: number;
}

export interface MachineBreakdown {
  machineId: string;
  machineName: string;
  totalCount: number;
  overdueCount: number;
  totalCost: number;
}

export interface AssigneeBreakdown {
  userId: string;
  userName: string;
  completedCount: number;
  avgCompletionHours: number;
}

export interface AnalyticsQuery {
  dateFrom: string;
  dateTo: string;
  machineId?: string;
  assignedToUserId?: string;
  workOrderTypeId?: number;
  topN?: number;
}

// --- Multi-Assignee (Phase: Multi-Assign) ---

export interface WorkOrderAssignee {
  userId: string;
  userName: string;
  role: number; // 1=Assignee, 2=Lead
  roleName: string;
  assignedAt: string;
  assignedByUserName?: string;
}

export interface AddAssigneesRequest {
  assignees: { userId: string }[];
}

export interface AssignTeamRequest {
  teamId: string | null;
}

export interface ChangeAssigneeRoleRequest {
  role: number;
}

// --- KPI Summary ---

export interface KpiSummary {
  activeCount: number;
  criticalCount: number;
  overdueCount: number;
  avgResolutionDays: number;
  doneThisWeekCount: number;
  doneThisWeekTotal: number;
}

// --- Assignable User (RC-5) ---

export interface AssignableUser {
  userId: string;
  userName: string;
  teamName?: string;
  openWorkOrderCount: number;
}

// --- Location (LOC-6) ---

export interface Location {
  id: string;
  name: string;
  parentLocationId?: string;
  parentLocationName?: string;
  city?: string;
  countryName?: string;
  isDefault?: boolean;
  machineCount?: number;
  children?: Location[];
}

// --- Incident Context (SR tab data) ---

/** Full SR detail for Incident Context tabs (fetched from Machinery API). */
export interface IncidentSrDetail {
  id: string;
  number: string;
  title: string;
  alarmCode?: string;
  urgency?: number;
  affectedPartCategoryId?: string;
  description?: string;
  notes?: string;
  createdByName?: string;
  createdDate?: string;
  statusCode: number;
  files?: { id?: string; name: string; size?: number; typeCode?: number }[];
  fileCount?: number;
  machineId?: string;
  machineModel?: string;
  locationName?: string;
}

// --- Slice state ---

export interface WorkOrdersState {
  workOrders: WorkOrder[];
  totalCount: number;
  loading: boolean;
  currentWorkOrder: WorkOrderDetail | null;
  currentLoading: boolean;
  summary: WorkOrderSummary | null;
  filters: WorkOrderFilters | null;
  myTasks: WorkOrder[];
  myTasksLoading: boolean;
  recurrencePreview: string[];
  generatingRecurring: boolean;
  teams: Team[];
  teamsLoading: boolean;
  teamWorkload: TeamWorkload | null;
  assignableUsers: AssignableUser[];
  assignableUsersLoading: boolean;
  analytics: WorkOrderAnalytics | null;
  analyticsLoading: boolean;
  scheduleWorkOrders: WorkOrder[];
  scheduleLoading: boolean;
  kpiSummary: KpiSummary | null;
  kpiLoading: boolean;
  error: string | null;
}
